/**
 * Settings → Keyboard shortcuts (desktop only), the web twin of the macOS
 * KeyboardShortcutsSettingsView: search, one group per category, click a row
 * to record a new combo, reset one or all.
 */
import { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  DEFAULT_SHORTCUTS,
  detectConflict,
  formatKeyCombo,
  getShortcutDef,
  type ShortcutCategory,
} from '../../lib/shortcuts'
import { useShortcutsStore } from '../../store/shortcuts'
import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { SettingsEmpty, SettingsRow, SettingsSection } from './parts'

const CATEGORIES: ShortcutCategory[] = ['General', 'Layout', 'Editor']

export function ShortcutsSettings() {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [conflict, setConflict] = useState<string | null>(null)

  const resolved = useShortcutsStore((s) => s.resolved)
  const overrides = useShortcutsStore((s) => s.overrides)
  const setBinding = useShortcutsStore((s) => s.setBinding)
  const resetBinding = useShortcutsStore((s) => s.resetBinding)
  const resetAll = useShortcutsStore((s) => s.resetAll)

  // Record the next combo. Window capture runs before the dialog's own
  // Escape handler and every global shortcut, so nothing else sees the keys.
  const handleCapture = useCallback(
    (e: KeyboardEvent) => {
      if (!editingId) return
      if (['Meta', 'Control', 'Alt', 'Shift'].includes(e.key)) return
      e.preventDefault()
      e.stopPropagation()

      if (e.key === 'Escape') {
        setEditingId(null)
        setConflict(null)
        return
      }

      if (!e.metaKey && !e.ctrlKey && !e.altKey) return

      const parts: string[] = []
      if (e.metaKey || e.ctrlKey) parts.push('mod')
      if (e.altKey) parts.push('alt')
      if (e.shiftKey) parts.push('shift')
      parts.push(e.key.toLowerCase())
      const combo = parts.join('+')

      const conflictId = detectConflict(editingId, combo, resolved)
      if (conflictId) {
        const def = getShortcutDef(conflictId)
        setConflict(`Conflicts with "${def?.label ?? conflictId}"`)
        return
      }

      setBinding(editingId, combo)
      setEditingId(null)
      setConflict(null)
    },
    [editingId, resolved, setBinding],
  )

  useEffect(() => {
    if (!editingId) return
    window.addEventListener('keydown', handleCapture, true)
    return () => window.removeEventListener('keydown', handleCapture, true)
  }, [editingId, handleCapture])

  const q = query.toLowerCase().trim()
  const filtered = q
    ? DEFAULT_SHORTCUTS.filter(
        (s) =>
          s.label.toLowerCase().includes(q) ||
          s.category.toLowerCase().includes(q) ||
          formatKeyCombo(resolved[s.id] ?? s.defaultKeys).toLowerCase().includes(q),
      )
    : DEFAULT_SHORTCUTS

  return (
    <>
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Icon
            name="search"
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('shortcuts.searchPlaceholder')}
            className="w-full rounded-full border border-border bg-canvas py-2 pl-8 pr-3 text-sm text-ink placeholder:text-placeholder focus:border-accent focus:outline-none"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            resetAll()
            setEditingId(null)
            setConflict(null)
          }}
        >
          {t('shortcuts.resetAll')}
        </Button>
      </div>

      {filtered.length === 0 && (
        <SettingsEmpty
          title={
            <Trans
              i18nKey="shortcuts.noMatches"
              values={{ query }}
              components={{ strong: <strong /> }}
            />
          }
        />
      )}

      {CATEGORIES.map((cat) => {
        const items = filtered.filter((s) => s.category === cat)
        if (items.length === 0) return null
        return (
          <SettingsSection key={cat} title={cat}>
            {items.map((shortcut) => {
              const isEditing = editingId === shortcut.id
              const combo = resolved[shortcut.id] ?? shortcut.defaultKeys
              return (
                <SettingsRow
                  key={shortcut.id}
                  label={shortcut.label}
                  subtitle={
                    isEditing && conflict ? (
                      <span className="text-danger">{conflict}</span>
                    ) : undefined
                  }
                  trailing={
                    // The recorder field, as on the Mac: click it, press the combo.
                    <span className="flex shrink-0 items-center gap-1.5">
                      {!isEditing && shortcut.id in overrides && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted hover:text-ink"
                          onClick={() => resetBinding(shortcut.id)}
                          title={t('shortcuts.resetToDefault')}
                        >
                          <Icon name="restore" size={12} />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className={isEditing ? 'border-accent text-xs italic text-accent' : 'font-chrome text-[12px] tracking-wide'}
                        onClick={() => {
                          setEditingId(isEditing ? null : shortcut.id)
                          setConflict(null)
                        }}
                      >
                        {isEditing ? t('shortcuts.pressNew') : formatKeyCombo(combo)}
                      </Button>
                    </span>
                  }
                />
              )
            })}
          </SettingsSection>
        )
      })}
    </>
  )
}
